import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, XCircle, CheckCircle } from 'lucide-react';
import { saveSession, CustomerInfo, CustomerSession } from '../api/auth';
import { useAuth } from '../App';
import { apiGetWithToken } from '../api/client';

interface GetSessionResponse {
  customer: CustomerInfo;
  session: {
    expiresAt: string;
  };
}

export default function ImpersonatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCustomer } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    async function handleImpersonation() {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid impersonation link. No token provided.');
        setStatus('error');
        return;
      }

      try {
        // The token is already a valid session token from admin impersonation
        // Use it directly to validate and get customer info
        const response = await apiGetWithToken<GetSessionResponse>(
          '/api/customer/auth/session',
          token
        );

        // Create session object
        const session: CustomerSession = {
          token,
          expiresAt: response.session.expiresAt,
        };

        // Save session and customer to localStorage
        saveSession(session, response.customer);
        setCustomer(response.customer);
        setStatus('success');

        // Redirect to dashboard after brief success message
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to validate session');
        setStatus('error');
      }
    }

    handleImpersonation();
  }, [searchParams, navigate, setCustomer]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-accent-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Setting up your session...</h2>
            <p className="text-gray-600">Please wait while we sign you in.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You're signed in!</h2>
            <p className="text-gray-600">Redirecting to the portal...</p>
            <p className="text-xs text-amber-600 mt-3">Admin view - 1 hour session</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Session invalid</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
